import { clamp, debounce } from "lodash-es";
import type { StateCreator, StoreApi } from "zustand";

import { MIN_VISIBLE_AREA_WIDTH, SVG_STYLES } from "../../components/overview/settings.ts";
import { GROUP_HEADER_HEIGHT, HIGHLIGHT_DELAY } from "../../settings.ts";
import type { CanvasCoordinate } from "../../types.ts";
import type { GenomeViewerStore } from "../store.ts";
import { handleScrollingEnd } from "./user-events-slice.ts";

export interface CanvasState {
  /** DNA base per pixel. This is the zoom level */
  basePerPixel: number;
  /** X position of the scroll in pixels. The canvas is not 100% of the tracks height and width. */
  scrollX: number;
  /** Y position of the scroll in pixels. The canvas is not 100% of the tracks height and width. */
  scrollY: number;
  highlightedOrgId: number | undefined;
}

export interface CanvasActions {
  setZoom: (basePerPixel: number) => void;
  getOriginOffset(): number;
  getMaxWidth(): [number, number];
  moveCanvasScrollX: (offsetX: number, scale: number) => void;
  getScrollX: (offsetX: number, scale: number, canvasWidth: number, originOffset: number) => number;
  getTrackGroupIndexByY: (y: number) => number | undefined;
  navigateToPosition: ({ x, y }: CanvasCoordinate) => void;
  navigateToTrackGroup: (orgId: number) => void;
}

export type CanvasSlice = CanvasActions & CanvasState;

export const initialCanvasState: CanvasState = {
  basePerPixel: 250,
  scrollX: 0,
  scrollY: 0,
  highlightedOrgId: undefined,
};

export const createCanvasSlice: StateCreator<GenomeViewerStore, [], [], CanvasSlice> = (set, get) => ({
  ...initialCanvasState,
  setZoom(basePerPixel) {
    const { basePerPixel: currentBasePerPixel, canvas, scrollX } = get();
    if (basePerPixel === currentBasePerPixel) {
      return;
    }

    const centerCoordinateX = (scrollX + canvas!.width / 2) * currentBasePerPixel;

    const newScrollX = centerCoordinateX / basePerPixel - canvas!.width / 2;

    set({
      basePerPixel,
      scrollX: newScrollX,
    });
    get().refreshTracks();
  },
  getOriginOffset() {
    const { trackGroups, basePerPixel, scrollX } = get();

    const minBaseShift = Math.min(
      ...Array.from(trackGroups.values())
        .filter((trackGroup) => trackGroup.graphics.isVisible)
        .map((trackGroup) => trackGroup?.graphics.baseShift ?? 0),
    );

    return Math.min(scrollX, minBaseShift / basePerPixel);
  },
  getMaxWidth() {
    const { trackGroups, basePerPixel, scrollX, ctx } = get();

    const maxTrackGroupWidth =
      (Math.max(
        ...Array.from(trackGroups.values())
          .filter((trackGroup) => trackGroup.graphics.isVisible)
          .map((trackGroup) => (trackGroup?.graphics.length ?? 0) + (trackGroup?.graphics.baseShift ?? 0)),
      ) +
        1) /
      basePerPixel;

    return [
      maxTrackGroupWidth,
      Math.max(scrollX + (ctx?.canvas.width ?? 0), maxTrackGroupWidth) - get().getOriginOffset(),
    ];
  },
  moveCanvasScrollX(offsetX, scale) {
    const { ctx, getOriginOffset, getMaxWidth, scrollX } = get();

    const newScrollX = clamp(
      get().getScrollX(offsetX, scale, ctx?.canvas.width ?? 0, getOriginOffset()),
      getOriginOffset(),
      Math.max(getMaxWidth()[0] - (ctx?.canvas.width ?? 0), scrollX),
    );

    set({
      isDraggingRegionIndicator: true,
      scrollX: newScrollX,
    });
  },
  getScrollX(offsetX, scale, canvasWidth, originOffset) {
    return (
      (offsetX -
        Math.round(Math.max(canvasWidth * scale, MIN_VISIBLE_AREA_WIDTH) - SVG_STYLES.visibleRegion.strokeWidth / 2) /
          2) /
        scale +
      originOffset
    );
  },
  getTrackGroupIndexByY(y) {
    let index = 0;
    let offsetY = 0;
    const { trackGroups, tracks } = get();

    for (const [_, trackGroup] of [...trackGroups.entries()].filter(([_, group]) => group.graphics.isVisible)) {
      const visibleTracks = trackGroup.trackIds
        .map((trackId) => tracks.get(trackId))
        .filter((track): track is NonNullable<typeof track> => !!track?.isVisible);

      const height = [...visibleTracks.values()].reduce((acc, track) => acc + track.height, GROUP_HEADER_HEIGHT);

      if (y >= offsetY && y < offsetY + height) {
        return index;
      }
      offsetY += height;
      index++;
    }
    return undefined;
  },

  navigateToPosition({ x, y }) {
    handleScrollingEnd(set);
    set({ scrollX: x, scrollY: y, positionToNavigate: undefined, isScrolling: true });
    get().refreshTracks();
  },
  navigateToTrackGroup(orgId) {
    // get Y of group
    let offsetY = 0;
    for (const trackGroup of get().trackGroups.values()) {
      if (trackGroup.organismId === orgId) {
        break;
      }
      if (trackGroup.graphics.isVisible) {
        offsetY +=
          GROUP_HEADER_HEIGHT +
          trackGroup.trackIds
            .map((trackId) => {
              const track = get().tracks.get(trackId);
              return (track?.isVisible ? track.height : 0) ?? 0;
            })
            .reduce((trackGroupHeight, trackHeight) => trackGroupHeight + trackHeight, 0);
      }
    }

    handleHighlightEnd(get, set);
    set({ scrollY: offsetY, highlightedOrgId: orgId });
    get().refreshTracks();
  },
});

const handleHighlightEnd = debounce(
  (get: StoreApi<GenomeViewerStore>["getState"], set: StoreApi<GenomeViewerStore>["setState"]) => {
    set({ highlightedOrgId: undefined });
    get().refreshMain();
  },
  HIGHLIGHT_DELAY,
);
